import React, { useState } from 'react';
import { TextField, Checkbox, FormControlLabel, Autocomplete, Box, Typography } from '@mui/material';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { ThemeProvider } from '@mui/material/styles';
import { muiTheme } from '../../theme/muiTheme';
import { InvoiceRecord, StoreRecord, SupplierRecord } from '../../types';
import {
  AssignmentSection,
  ClassificationSelector,
  CreditNoteSection,
  SupplierAutoComplete
} from '../molecules';
import { FileInfoSection } from '../organisms/invoice';
import { useWorkflowPermissions } from '../../hooks/useWorkflowPermissions';
import { useAssignmentLogic } from '../../hooks/useAssignmentLogic';
import { WorkflowUIField } from '../../config/workflow-field-permissions';
import { UserRole } from '../../config/workflow-config';
import { InvoiceStatus, InvoiceClassification } from '../../config/invoice-config';
import { trpc } from '../../lib/trpc';

interface InvoiceFormTemplateProps {
  invoice: InvoiceRecord;
  formData: Partial<InvoiceRecord>;
  currentStatus: string;
  userRole: UserRole;
  currentUserId?: string;
  assignedToUserId?: string;
  selectedStore: StoreRecord | null;
  selectedSupplier: SupplierRecord | null;
  setSelectedSupplier: (supplier: SupplierRecord | null) => void;
  storesData?: { data: StoreRecord[] };
  suppliersData?: { data: SupplierRecord[] };
  isLoadingStores: boolean;
  isLoadingSuppliers: boolean;

  // Event handlers
  onInputChange: (field: keyof InvoiceRecord, value: string) => void;
  onStoreChange: (store: StoreRecord | null) => void;
  onFormDataChange: (updater: (prev: Partial<InvoiceRecord>) => Partial<InvoiceRecord>) => void;
  onAssignToMe: () => void;
  onUnassign: () => void;

  // Workflow permissions are now handled internally

  // Loading states
  isAssigning: boolean;
  isUnassigning: boolean;

  // Workflow system toggle
  useWorkflowSystem?: boolean;
}

/**
 * InvoiceFormTemplate - A reusable template for invoice form layouts
 *
 * This template provides a consistent structure for invoice forms with:
 * - Assignment controls for applicable statuses
 * - Dynamic field rendering based on status and permissions
 * - Workflow-specific field visibility logic using the new workflow system
 * - Credit note section handling
 * - File information display
 *
 * Used by: InvoiceEditPage, InvoiceCreatePage, and similar invoice management interfaces
 */
export const InvoiceFormTemplate: React.FC<InvoiceFormTemplateProps> = ({
  invoice,
  formData,
  currentStatus,
  userRole,
  currentUserId,
  assignedToUserId,
  selectedStore,
  selectedSupplier,
  setSelectedSupplier,
  storesData,
  suppliersData,
  isLoadingStores,
  isLoadingSuppliers,
  onInputChange,
  onStoreChange,
  onFormDataChange,
  onAssignToMe,
  onUnassign,
  isAssigning,
  isUnassigning,
}) => {

  // State for supplier auto-complete
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [pendingSupplierChange, setPendingSupplierChange] = useState<{
    oldSupplier: SupplierRecord | null;
    newSupplier: SupplierRecord;
  } | null>(null);
  const [deleteUnverifiedSupplier, setDeleteUnverifiedSupplier] = useState(true);

  // Handle supplier selection
  const handleSupplierChange = (supplier: SupplierRecord | null) => {
    if (!supplier) {
      setSelectedSupplier(null);
      onInputChange('supplier', '');
      onInputChange('supplier_abn', '');
      return;
    }

    // Check if this is a verification status change
    if (selectedSupplier && selectedSupplier.id === supplier.id) {
      // Just updating verification status
      setSelectedSupplier(supplier);
      return;
    }

    // Show dialog for ALL supplier changes
    setPendingSupplierChange({
      oldSupplier: selectedSupplier,
      newSupplier: supplier,
    });
    setShowVerificationDialog(true);
  };

  // Handle confirmation of supplier replacement
  const handleConfirmSupplierReplacement = () => {
    if (pendingSupplierChange) {
      const { newSupplier } = pendingSupplierChange;
      setSelectedSupplier(newSupplier);
      onInputChange('supplier', newSupplier.name);
      onInputChange('supplier_abn', newSupplier.abn || '');
      setShowVerificationDialog(false);
      setPendingSupplierChange(null);
      setDeleteUnverifiedSupplier(true); // Reset checkbox to default
    }
  };

  // Handle cancellation of supplier replacement
  const handleCancelSupplierReplacement = () => {
    setShowVerificationDialog(false);
    setPendingSupplierChange(null);
    setDeleteUnverifiedSupplier(true); // Reset checkbox to default
  };


  // Convert string status to enum
  const statusMap: Record<string, InvoiceStatus> = {
    'unprocessed': InvoiceStatus.UNPROCESSED,
    'adding': InvoiceStatus.ADDING,
    'new_products': InvoiceStatus.NEW_PRODUCTS,
    'ir': InvoiceStatus.IR,
    'invoicing': InvoiceStatus.INVOICING,
    'head_office_stock': InvoiceStatus.HEAD_OFFICE_STOCK,
    'expense_invoices': InvoiceStatus.EXPENSE_INVOICES,
    'entering_stock': InvoiceStatus.ENTERING_STOCK,
    'entering_expense': InvoiceStatus.ENTERING_EXPENSE,
    'credit_note': InvoiceStatus.CREDIT_NOTE,
    'credit_entering': InvoiceStatus.CREDIT_ENTERING,
    'completed': InvoiceStatus.COMPLETED
  };
  const statusEnum = statusMap[currentStatus.toLowerCase()] || InvoiceStatus.UNPROCESSED;

  // Use new workflow permissions system
  const effectiveUserRole = userRole || UserRole.STOCK;
  const permissions = useWorkflowPermissions(
    statusEnum,
    (invoice.classification as InvoiceClassification) || 'deliver_direct',
    effectiveUserRole,
    currentUserId,
    assignedToUserId
  );

  // Use assignment logic hook for better separation of concerns
  const { wasUserPreviouslyAssigned, isAssignmentRequired } = useAssignmentLogic(
    currentUserId,
    invoice
  );

  // Helper functions to map new permissions to component needs
  const isFieldVisible = (field: WorkflowUIField): boolean => {
    return permissions.isFieldVisible(field);
  };

  const isFieldEditable = (field: WorkflowUIField): boolean => {
    return permissions.isFieldEditable(field);
  };

  const handleStoreChange = (_: unknown, newValue: StoreRecord | null) => {
    onStoreChange(newValue);
    onInputChange('store_id', newValue?.id || '');
  };
  


  return (
    <div className="p-6">
      {/* Assignment Controls - Show for statuses that require assignment */}
      {isAssignmentRequired && (
        <AssignmentSection
          invoice={invoice}
          wasUserPreviouslyAssigned={wasUserPreviouslyAssigned || false}
          userRole={effectiveUserRole}
          onAssignToMe={onAssignToMe}
          onUnassign={onUnassign}
          isAssigning={isAssigning}
          isUnassigning={isUnassigning}
        />
      )}

      <h2 className="text-lg font-semibold text-secondary-900 mb-6">
        Invoice Details
      </h2>

      <ThemeProvider theme={muiTheme}>
        <div className="space-y-6">
          {/* Invoice Classification - Only editable in UNPROCESSED status */}
          {isFieldVisible(WorkflowUIField.CLASSIFICATION) && (
            <ClassificationSelector
              classification={formData.classification || 'deliver_direct'}
              onChange={(classification: string) => onInputChange('classification', classification)}
              disabled={!isFieldEditable(WorkflowUIField.CLASSIFICATION)}
            />
          )}

          {/* New Stock Checkbox - Show in Adding status */}
          {statusEnum === InvoiceStatus.ADDING && isFieldVisible(WorkflowUIField.NEW_STOCK) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_new_stock || false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFormDataChange(prev => ({ ...prev, is_new_stock: e.target.checked }))}
                  size="small"
                  sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                  disabled={!isFieldEditable(WorkflowUIField.NEW_STOCK)}
                />
              }
              label={<span style={{ fontSize: '0.875rem' }}>New Stock</span>}
              sx={{ marginBottom: '0.5rem' }}
            />
          )}

          {/* Product Added Checkbox - Show in New Products status */}
          {statusEnum === InvoiceStatus.NEW_PRODUCTS && isFieldVisible(WorkflowUIField.PRODUCT_ADDED) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_product_added || false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const isChecked = e.target.checked;
                    onFormDataChange(prev => ({
                      ...prev,
                      is_product_added: isChecked
                    }));
                  }}
                  disabled={!isFieldEditable(WorkflowUIField.PRODUCT_ADDED)}
                  size="small"
                  sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }}
                />
              }
              label={<span style={{ fontSize: '0.875rem' }}>Product Added</span>}
              sx={{ marginBottom: '0.5rem' }}
            />
          )}

          {/* Supplier */}
          {isFieldVisible(WorkflowUIField.SUPPLIER) && (
            <div>
              <SupplierAutoComplete
                value={selectedSupplier}
                onChange={handleSupplierChange}
                disabled={!isFieldEditable(WorkflowUIField.SUPPLIER)}
                currentSupplierName={formData.supplier}
                currentSupplierAbn={formData.supplier_abn}
                suppliersData={suppliersData}
                isLoadingSuppliers={isLoadingSuppliers}
              />
              {/* Verification status indicator */}
              {selectedSupplier && !selectedSupplier.isVerified && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, color: 'warning.main' }}>
                  <AlertTriangle size={12} style={{ marginRight: '12px' }} />
                  <Typography variant="caption">Unverified Supplier</Typography>
                </Box>
              )}
            </div>
          )}

          {/* Invoice Number */}
          {isFieldVisible(WorkflowUIField.INVOICE_NUMBER) && (
            <TextField
              label="Invoice Number"
              variant="outlined"
              size="small"
              value={formData.invoice_number || ''}
              onChange={(e) => onInputChange('invoice_number', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.INVOICE_NUMBER)}
              sx={{ width: '100%' }}
            />
          )}

          {/* Invoice Date */}
          {isFieldVisible(WorkflowUIField.INVOICE_DATE) && (
            <TextField
              label="Invoice Date"
              type="date"
              variant="outlined"
              size="small"
              value={formData.invoice_date || ''}
              onChange={(e) => onInputChange('invoice_date', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.INVOICE_DATE)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: '100%' }}
            />
          )}

          {/* Due Date */}
          {isFieldVisible(WorkflowUIField.DUE_DATE) && (
            <TextField
              label="Due Date"
              type="date"
              variant="outlined"
              size="small"
              value={formData.due_date || ''}
              onChange={(e) => onInputChange('due_date', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.DUE_DATE)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: '100%' }}
            />
          )}

          {/* Total Amount */}
          {isFieldVisible(WorkflowUIField.TOTAL_AMOUNT) && (
            <TextField
              label="Total Amount"
              variant="outlined"
              size="small"
              value={formData.total_amount || ''}
              onChange={(e) => onInputChange('total_amount', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.TOTAL_AMOUNT)}
              sx={{ width: '100%' }}
            />
          )}

          {/* Supplier ABN */}
          {isFieldVisible(WorkflowUIField.SUPPLIER_ABN) && (
            <TextField
              label="Supplier ABN"
              variant="outlined"
              size="small"
              value={formData.supplier_abn || ''}
              onChange={(e) => onInputChange('supplier_abn', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.SUPPLIER_ABN)}
              sx={{ width: '100%' }}
            />
          )}

          {/* Notes - Always visible and editable for all roles */}
          {isFieldVisible(WorkflowUIField.NOTES) && (
            <TextField
              label="Notes"
              variant="outlined"
              size="small"
              multiline
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => onInputChange('notes', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.NOTES)}
              sx={{ width: '100%' }}
              placeholder="Add notes about this invoice..."
            />
          )}

          {/* Workflow-specific fields based on permissions */}
          {/* Store Autocomplete */}
          {isFieldVisible(WorkflowUIField.STORE_ID) && (
            <Autocomplete
              options={(storesData?.data || []).map(store => ({
                ...store,
                created_at: (store as StoreRecord & { createdAt?: string }).createdAt || '',
                updated_at: (store as StoreRecord & { updatedAt?: string }).updatedAt || ''
              })) as StoreRecord[]}
              getOptionLabel={(option) => option.name}
              value={selectedStore}
              onChange={handleStoreChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Store"
                  variant="outlined"
                  size="small"
                  sx={{ width: '100%' }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingStores}
              disabled={!isFieldEditable(WorkflowUIField.STORE_ID)}
              sx={{ width: '100%' }}
            />
          )}

          {/* PO Field */}
          {isFieldVisible(WorkflowUIField.PO) && (
            <TextField
              label="PO"
              variant="outlined"
              size="small"
              value={formData.po || ''}
              onChange={(e) => onInputChange('po', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.PO)}
              sx={{ width: '100%' }}
            />
          )}

          {/* SO Field */}
          {isFieldVisible(WorkflowUIField.SO) && (
            <TextField
              label="SO"
              variant="outlined"
              size="small"
              value={formData.so || ''}
              onChange={(e) => onInputChange('so', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.SO)}
              sx={{ width: '100%' }}
            />
          )}

          {/* IR Field */}
          {isFieldVisible(WorkflowUIField.IR) && (
            <TextField
              label="IR"
              variant="outlined"
              size="small"
              value={formData.ir || ''}
              onChange={(e) => onInputChange('ir', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.IR)}
              sx={{ width: '100%' }}
            />
          )}

          {/* INV Field */}
          {isFieldVisible(WorkflowUIField.INV) && (
            <TextField
              label="INV"
              variant="outlined"
              size="small"
              value={formData.inv || ''}
              onChange={(e) => onInputChange('inv', e.target.value)}
              disabled={!isFieldEditable(WorkflowUIField.INV)}
              sx={{ width: '100%' }}
            />
          )}

          {/* Credit Note Section */}
          {isFieldVisible(WorkflowUIField.CLAIM) && (
            <CreditNoteSection
              formData={formData}
              onChange={onInputChange}
              disabled={!isFieldEditable(WorkflowUIField.CLAIM)}
            />
          )}
        </div>
      </ThemeProvider>

      {/* Separator Line */}
      <div className="border-t border-secondary-200 my-6"></div>

      {/* File Information */}
      <FileInfoSection invoice={invoice} />

      {/* Supplier Change Confirmation Dialog */}
      {showVerificationDialog && pendingSupplierChange && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Supplier Change</h3>
                <p className="text-sm text-gray-500">This action will update the invoice</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-700 mb-3">
                You're about to change the supplier for this invoice:
              </p>

              {/* Current Supplier */}
              {pendingSupplierChange.oldSupplier && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-600">Current Supplier</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{pendingSupplierChange.oldSupplier.name}</p>
                      <p className="text-sm text-gray-600">ABN: {pendingSupplierChange.oldSupplier.abn || 'Not provided'}</p>
                    </div>
                    {!pendingSupplierChange.oldSupplier.isVerified && (
                      <div className="flex items-center">
                        <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
                        <span className="text-xs text-yellow-600">Unverified</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* New Supplier */}
              <div className="bg-blue-50 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-blue-600">New Supplier</p>
                  {pendingSupplierChange.newSupplier.isVerified ? (
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                      <span className="text-xs text-green-600">Verified</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
                      <span className="text-xs text-yellow-600">Unverified</span>
                    </div>
                  )}
                </div>
                <p className="font-medium text-gray-900">{pendingSupplierChange.newSupplier.name}</p>
                <p className="text-sm text-gray-600">ABN: {pendingSupplierChange.newSupplier.abn || 'Not provided'}</p>
              </div>

              {/* Warning notice for unverified supplier deletion */}
              {pendingSupplierChange.oldSupplier && !pendingSupplierChange.oldSupplier.isVerified && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex items-start mb-3">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> This will change the supplier from an unverified to a verified supplier.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="deleteUnverifiedSupplier"
                      checked={deleteUnverifiedSupplier}
                      onChange={(e) => setDeleteUnverifiedSupplier(e.target.checked)}
                      className="mt-1 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-yellow-300 rounded"
                    />
                    <label htmlFor="deleteUnverifiedSupplier" className="ml-3 text-sm text-yellow-800">
                      <strong>Automatically delete</strong> the unverified supplier "{pendingSupplierChange.oldSupplier.name}" after confirming this change.
                      <br />
                      <span className="text-xs text-yellow-600">(Only if it has no other invoices linked to it)</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelSupplierReplacement}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSupplierReplacement}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};
